import React, { useState, useRef } from "react";
import {
  ActionButton,
  Calendar,
  Callout,
  Dropdown,
  IDropdownOption,
  Stack,
  Text,
  DirectionalHint,
  IconButton,
} from "@fluentui/react";
import { DayOfWeek } from "@fluentui/react";
import { FilterTypes } from "./FilterComponent";
import { styles } from "./filterComponent.styles";

export type DateOperator = ">" | ">=" | "<" | "<=" | "=" | "!=";

interface DateRangeFilterProps {
  text: string;
  type: FilterTypes;
  clearable?: boolean;

  // For DateRange type (existing)
  selectedStartDate?: Date;
  selectedEndDate?: Date;

  // For DateTimeOperator type (new)
  selectedDate?: Date;
  selectedOperator?: DateOperator;

  // Combined onChange handler
  onChange:
    | ((startDate?: Date, endDate?: Date) => void)
    | ((date?: Date, operator?: DateOperator) => void);

  [key: string]: any;
}

const operatorOptions: IDropdownOption[] = [
  { key: ">", text: "after (>)" },
  { key: ">=", text: "on or after (>=)" },
  { key: "<", text: "before (<)" },
  { key: "<=", text: "on or before (<=)" },
  { key: "=", text: "on (=)" },
  { key: "!=", text: "not on (!=)" },
];

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  text,
  type,
  clearable = true,
  selectedStartDate,
  selectedEndDate,
  selectedDate,
  selectedOperator = ">=",
  onChange,
  ..._props
}) => {
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  const formatDate = (date?: Date): string => {
    if (!date) return "Select date";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDisplayText = (): string => {
    if (type === FilterTypes.DateTimeOperator) {
      if (selectedDate && selectedOperator) {
        return `${text} ${selectedOperator} ${formatDate(selectedDate)}`;
      }
      return `${text}`;
    } else {
      // DateRange type
      if (selectedStartDate && selectedEndDate) {
        return `${formatDate(selectedStartDate)} - ${formatDate(
          selectedEndDate,
        )}`;
      } else if (selectedStartDate) {
        return `From ${formatDate(selectedStartDate)}`;
      } else if (selectedEndDate) {
        return `Until ${formatDate(selectedEndDate)}`;
      }
      return `${text}`;
    }
  };

  const handleDateSelect = (date: Date) => {
    if (type === FilterTypes.DateTimeOperator) {
      if (onChange) {
        (onChange as (date?: Date, operator?: DateOperator) => void)(
          date,
          selectedOperator,
        );
      }
    } else {
      // DateRange type - handle start/end date logic
      if (onChange) {
        (onChange as (startDate?: Date, endDate?: Date) => void)(
          selectedStartDate,
          selectedEndDate,
        );
      }
    }
  };

  const handleStartDateSelect = (date: Date) => {
    if (type === FilterTypes.DateRange && onChange) {
      (onChange as (startDate?: Date, endDate?: Date) => void)(
        date,
        selectedEndDate,
      );
    }
  };

  const handleEndDateSelect = (date: Date) => {
    if (type === FilterTypes.DateRange && onChange) {
      (onChange as (startDate?: Date, endDate?: Date) => void)(
        selectedStartDate,
        date,
      );
    }
  };

  const handleOperatorChange = (
    event: React.FormEvent<HTMLDivElement>,
    option?: IDropdownOption,
  ) => {
    if (option && type === FilterTypes.DateTimeOperator && onChange) {
      (onChange as (date?: Date, operator?: DateOperator) => void)(
        selectedDate,
        option.key as DateOperator,
      );
    }
  };

  const handleClear = () => {
    if (type === FilterTypes.DateTimeOperator && onChange) {
      (onChange as (date?: Date, operator?: DateOperator) => void)(
        undefined,
        ">=",
      );
    } else if (onChange) {
      (onChange as (startDate?: Date, endDate?: Date) => void)(
        undefined,
        undefined,
      );
    }
  };

  const hasSelection =
    type === FilterTypes.DateTimeOperator
      ? selectedDate
      : selectedStartDate || selectedEndDate;

  return (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
      <Stack
        horizontal
        verticalAlign="center"
        className={styles.dateFilterButtonContainer}
      >
        <div ref={buttonRef}>
          <ActionButton
            text={getDisplayText()}
            iconProps={{ iconName: "Calendar" }}
            onClick={() => setIsCalloutVisible(!isCalloutVisible)}
            styles={{
              root:
                type === FilterTypes.DateTimeOperator
                  ? styles.dateFilterButtonOperator
                  : styles.dateFilterButton,
              textContainer: styles.dateFilterButtonText,
            }}
          />
        </div>

        {clearable && hasSelection && (
          <IconButton
            iconProps={{ iconName: "Clear", style: { fontSize: 12 } }}
            title="Clear filter"
            onClick={handleClear}
            className={styles.clearButton}
          />
        )}
      </Stack>

      {isCalloutVisible && buttonRef.current && (
        <Callout
          target={buttonRef.current}
          onDismiss={() => setIsCalloutVisible(false)}
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible={false}
        >
          <Stack
            tokens={{ childrenGap: 12 }}
            styles={{ root: styles.calloutContent }}
          >
            {type === FilterTypes.DateTimeOperator ? (
              <>
                <Text
                  variant="mediumPlus"
                  styles={{ root: styles.filterTitle }}
                >
                  {text} Filter
                </Text>

                <Stack tokens={{ childrenGap: 8 }}>
                  <Text variant="small">Operator:</Text>
                  <Dropdown
                    options={operatorOptions}
                    selectedKey={selectedOperator}
                    onChange={handleOperatorChange}
                    styles={{ root: styles.operatorDropdown }}
                  />
                </Stack>

                <Stack tokens={{ childrenGap: 8 }}>
                  <Text variant="small">Date:</Text>
                  <Calendar
                    onSelectDate={handleDateSelect}
                    value={selectedDate}
                    firstDayOfWeek={DayOfWeek.Sunday}
                    showGoToToday={true}
                    highlightCurrentMonth={true}
                  />
                </Stack>
              </>
            ) : (
              <>
                <Text
                  variant="mediumPlus"
                  styles={{ root: styles.filterTitle }}
                >
                  {text} Range
                </Text>

                <Stack horizontal tokens={{ childrenGap: 16 }}>
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant="small">Start Date:</Text>
                    <Calendar
                      onSelectDate={handleStartDateSelect}
                      value={selectedStartDate}
                      maxDate={selectedEndDate}
                      firstDayOfWeek={DayOfWeek.Sunday}
                      showGoToToday={true}
                      highlightCurrentMonth={true}
                    />
                  </Stack>

                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant="small">End Date:</Text>
                    <Calendar
                      onSelectDate={handleEndDateSelect}
                      value={selectedEndDate}
                      minDate={selectedStartDate}
                      firstDayOfWeek={DayOfWeek.Sunday}
                      showGoToToday={true}
                      highlightCurrentMonth={true}
                    />
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Callout>
      )}
    </Stack>
  );
};

export default DateRangeFilter;
